/** @jsx jsx */

import { jsx } from '@emotion/core';
import { TimePeriodDays } from 'app/developer-v3/hooks/useIntegrationInsights';
import { ReactNode, useState } from 'react';
import DateRangeDropdown from '../DateRangeDropdown';
import { defaultInitialSelectedItem, timePeriodDaysItems } from '../utils';
import { BaseTile, BaseTileState } from './BaseTile';
import { Icon, IconName } from '@zapier/design-system';
import { CommonTimeSeriesChart } from 'app/developer-v3/components/CommonChart/CommonTimeSeriesChart';

type DailyLineChartTileProps = {
  data: { dateString: string; value: number }[];
  onSelect: (value: TimePeriodDays) => void;
  dataTestId?: string;
  /** e.g. Daily Active Zaps */
  headerContent: string;
  /** Zinnia icon name */
  icon: IconName;
  /** Color of the line in the chart */
  lineColor: string;
  tooltipContent: ReactNode;
  /** e.g. Zap Used Date */
  xAxisLabel: string;
  /** e.g. Count Activated Users */
  yAxisLabel: string;
} & Omit<BaseTileState, 'isNoData'>;

export default function DailyLineChartTile({
  data,
  isError,
  onSelect,
  headerContent,
  icon,
  lineColor,
  tooltipContent,
  xAxisLabel,
  yAxisLabel,
  dataTestId,
}: DailyLineChartTileProps) {
  const [isRenderError, setIsRenderError] = useState(false);

  return (
    <BaseTile
      dataTestId={dataTestId}
      dropdowns={[
        <DateRangeDropdown
          initialSelectedItem={defaultInitialSelectedItem}
          items={timePeriodDaysItems}
          onSelect={onSelect}
        />,
      ]}
      headerContent={headerContent}
      // use the chart loader prop to handle display of loading state
      icon={<Icon name={icon} size="24px" />}
      isError={isError || isRenderError}
      isLoading={false}
      isNoData={!(data && data.length > 0)}
      toolTipContent={tooltipContent}
    >
      <CommonTimeSeriesChart
        chartType="LineChart"
        colors={[lineColor]}
        data={data}
        hAxisFormat="MMM dd"
        setHasRenderError={setIsRenderError}
        xAxisLabel={xAxisLabel}
        yAxisLabel={yAxisLabel}
      />
    </BaseTile>
  );
}
